import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { api, setSEO } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

const SUBJECTS = [
  ["general", "General"],
  ["press", "Press / Editorial"],
  ["wholesale", "Wholesale"],
  ["contraband", "Contraband Inquiry"],
  ["support", "Support / Defect"],
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [state, setState] = useState({ status: "idle", message: "" });

  useEffect(() => {
    setSEO({
      title: "Contact | FEMBOT NEX",
      description: "Transmit to FEMBOT NEX. Press, wholesale, support, and contraband inquiries.",
    });
  }, []);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setState({ status: "loading", message: "" });
    try {
      const { data } = await api.post("/contact", form);
      setState({ status: "success", message: data.message });
      setForm({ name: "", email: "", subject: "general", message: "" });
    } catch (err) {
      setState({
        status: "error",
        message: formatApiError(err.response?.data?.detail) || "TRANSMISSION FAILED.",
      });
    }
  }

  return (
    <div data-testid="contact-page" className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-24">
      <div className="grid md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="section-label">// TRANSMIT</div>
          <h1 className="mt-3 font-display text-5xl md:text-6xl tracking-tight leading-[0.9]">
            Open a <span className="text-[#ff1493]">channel</span>.
          </h1>
          <p className="mt-6 text-[#a3a3a3] leading-relaxed">
            Send a transmission. We'll reply when able. For order issues, please include your order code if applicable.
          </p>
          <div className="mt-8 space-y-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#6b6b6b]">
            <div>NODE // fembotnex.com</div>
            <div>ENCRYPTED // TLS_1.3</div>
            <div>RESPONSE TIME // 24-72h</div>
          </div>
        </div>

        <div className="md:col-span-7 border border-[#262626] bg-black p-6 md:p-8">
          <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Name</label>
                <input
                  data-testid="contact-name"
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="field mt-2"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Email</label>
                <input
                  data-testid="contact-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="field mt-2"
                  placeholder="email"
                />
              </div>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Subject</label>
              <select
                data-testid="contact-subject"
                value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                className="field mt-2"
              >
                {SUBJECTS.map(([k, label]) => (
                  <option key={k} value={k} className="bg-black">
                    {label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#ff1493]">// Message</label>
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={(e) => update("message", e.target.value)}
                className="field mt-2 resize-y"
                placeholder="Type your transmission…"
              />
            </div>
            <button type="submit" data-testid="contact-submit" disabled={state.status === "loading"} className="btn-primary justify-center self-start">
              {state.status === "loading" ? "Transmitting…" : "Transmit"}{" "}
              <ArrowRight size={14} strokeWidth={1.5} />
            </button>
            {state.message && (
              <p
                data-testid="contact-message-feedback"
                className={`font-mono text-[11px] tracking-[0.18em] uppercase ${
                  state.status === "success" ? "text-[#00ff41]" : "text-[#ff3b30]"
                }`}
              >
                // {state.message}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
